import { useState } from "react";
import { ActualProject } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/ActualProject/ActualProject";
import { AssignedGoal } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/AssignedGoal/AssignedGoal";
import { AvatarSection } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/AvatarSection/AvatarSection";
import { CollaboratorExperience } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/CollaboratorExperience/CollaboratorExperience";
import { ProgressPlan } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/ProgresPlan/ProgresPlan";
import {
  DEVELOPMENT_TITLE_LABEL,
  PERFORMANCE_LABEL,
  RESUME_LABEL,
} from "../../../constants/Dictionary";
import { StyledLoaderContainer } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/SchedaCM/SchedaCM.styled";
import { CollaboratorInfo } from "../../../types/AppTypes";
import { Loader } from "../../core/Loader/Loader";
import { Panel } from "../../core/Panel/Panel";
import { OptionItem, TextSwitch } from "../../TextSwitch/TextSwitch";
import { StyledGridComparison, StyledRow } from "./ResumeComparison.styled";
import { PendingCalibration } from "../CalibrationModal";
import { useAppSelector } from "../../../hooks/store.hook";
import { useCollaboratorResume } from "../../../hooks/collaboratorResume.hook";
import { useCmSheetGoals } from "../../../hooks/cmSheet/goals.hook";
import { getInternalGradeLabels } from "../../../utils/orgFunctions.utils";
import { StyledWorkInformationBody } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/Resume.styled";
import { ProfessionalWay } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/ProfessionalWay/ProfessionalWay";
import { Divider } from "../../core/Divider/Divider";
import { BusinessAreaManagerComment } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/BusinessAreaManagerComment/BusinessAreaManagerComment";
import { InternalGradeView } from "../../../screens/CollaboratorDetail/CollaborProfileTabs/Resume/InternalGrade/InternalGradeView";
import { useCollaboratorCmSheets } from "../../../hooks/collaboratorProfile.hook";

interface ResumeComparisonProps {
  currentCollaborator: CollaboratorInfo | null;
  comparisonCollaborator: CollaboratorInfo | null;
  isOpen: boolean;
  onClose: () => void;
  width?: number;
  fullHeight?: boolean;
  pendingCalibration?: PendingCalibration;
}

export const ResumeComparison = ({
  currentCollaborator,
  comparisonCollaborator,
  isOpen,
  onClose,
  width,
  fullHeight,
  pendingCalibration,
}: ResumeComparisonProps) => {
  const { config: configYears } = useAppSelector((state) => state.config);

  const currentConfigYear = configYears.find((item) => item.current)?.year;

  const [selectedYear, setSelectedYear] = useState<OptionItem<number>>({
    value: currentConfigYear ?? 0,
    label: currentConfigYear?.toString() ?? "",
  });

  const { data: comparisonCmSheets, isFetching: comparisonCmSheetsLoading } =
    useCollaboratorCmSheets(comparisonCollaborator?.id ?? null, isOpen);

  const {
    data: cmSheetsCollaboratorViewed,
    isFetching: cmSheetsCollaboratorViewedLoading,
  } = useCollaboratorCmSheets(currentCollaborator?.id ?? null, isOpen);

  const {
    data: dataCollaboratorResumeViewed,
    isFetching: loadingCollaboratorResumeViewed,
  } = useCollaboratorResume(currentCollaborator?.id ?? null, isOpen);

  const {
    data: goalsDataCurrentCollaborator,
    isFetching: loadingGoalsCurrentCollaborator,
  } = useCmSheetGoals(currentCollaborator?.id ?? 0);

  const {
    data: dataCollaboratorResumeComparison,
    isFetching: loadingCollaboratorResumeComparison,
  } = useCollaboratorResume(comparisonCollaborator?.id ?? null, isOpen);

  const {
    data: goalsDataComparisonCollaborator,
    isFetching: loadingGoalsComparisonCollaborator,
  } = useCmSheetGoals(comparisonCollaborator?.id ?? 0);

  const resumeDataYearCollaboratorViewed = dataCollaboratorResumeViewed?.find(
    (item) => {
      return item.year === selectedYear.value;
    },
  );

  const resumeDataYearCollaboratorComparison =
    dataCollaboratorResumeComparison?.find(
      (item) => item.year === selectedYear.value,
    );

  const resumeTextSwitch = [
    { value: 1, label: DEVELOPMENT_TITLE_LABEL },
    { value: 2, label: PERFORMANCE_LABEL },
  ];

  const [selectedOption, setSelectedOption] = useState<OptionItem<number>>({
    value: resumeTextSwitch[0]?.value ?? 0,
    label: resumeTextSwitch[0]?.label ?? "",
  });

  const showLoader =
    loadingCollaboratorResumeViewed ||
    loadingCollaboratorResumeComparison ||
    cmSheetsCollaboratorViewedLoading ||
    comparisonCmSheetsLoading ||
    loadingGoalsCurrentCollaborator ||
    loadingGoalsComparisonCollaborator;

  const textSwitchOptions = dataCollaboratorResumeViewed?.map((item) => ({
    label: item.year.toString(),
    value: item.year,
  }));

  const startInternalGradeCollaboratorViewed = getInternalGradeLabels(
    configYears,
    resumeDataYearCollaboratorViewed?.internalGrade?.start,
  );

  const startInternalGradeCollaboratorComparison = getInternalGradeLabels(
    configYears,
    resumeDataYearCollaboratorComparison?.internalGrade?.start,
  );

  const endInternalGradeCollaboratorViewed = getInternalGradeLabels(
    configYears,
    resumeDataYearCollaboratorViewed?.internalGrade?.end,
  );

  const endInternalGradeCollaboratorComparison = getInternalGradeLabels(
    configYears,
    resumeDataYearCollaboratorComparison?.internalGrade?.end,
  );

  return (
    <Panel
      fullHeight={fullHeight}
      isOpen={isOpen}
      onClose={onClose}
      title={RESUME_LABEL}
      width={width}
      rightComponent={
        textSwitchOptions?.length ? (
          <TextSwitch
            selectedOption={selectedYear}
            setSelectedOption={setSelectedYear}
            options={textSwitchOptions}
          />
        ) : undefined
      }
    >
      {isOpen ? (
        <StyledGridComparison>
          {showLoader ? (
            <>
              <StyledLoaderContainer>
                <Loader loading={true} className="cm-sheet-loader" />
              </StyledLoaderContainer>
              <StyledLoaderContainer>
                <Loader loading={true} className="cm-sheet-loader" />
              </StyledLoaderContainer>
            </>
          ) : currentCollaborator && comparisonCollaborator ? (
            <>
              <StyledRow className="sticky-row top-row-1">
                <div className="section-box">
                  <AvatarSection
                    collaborator={currentCollaborator}
                    isComparison
                  />
                </div>
                <div className="section-box">
                  <AvatarSection
                    collaborator={comparisonCollaborator}
                    isComparison
                  />
                </div>
              </StyledRow>
              <StyledRow className="sticky-row top-row-2">
                <div className="section-box">
                  <CollaboratorExperience
                    collaborator={currentCollaborator}
                    data={resumeDataYearCollaboratorViewed ?? null}
                    isComparison
                  />
                </div>
                <div className="section-box">
                  <CollaboratorExperience
                    collaborator={comparisonCollaborator}
                    data={resumeDataYearCollaboratorComparison ?? null}
                    isComparison
                  />
                </div>
              </StyledRow>
              <StyledRow className="sticky-row top-row-3">
                <div className="section-box">
                  <InternalGradeView
                    startInternalGrade={startInternalGradeCollaboratorViewed}
                    isComparisonComponent
                  />
                </div>
                <div className="section-box">
                  <InternalGradeView
                    startInternalGrade={
                      startInternalGradeCollaboratorComparison
                    }
                    isComparisonComponent
                  />
                </div>
              </StyledRow>
              <StyledRow>
                <div className="section-box">
                  <ActualProject
                    data={resumeDataYearCollaboratorViewed ?? null}
                  />
                </div>
                <div className="section-box">
                  <ActualProject
                    data={resumeDataYearCollaboratorComparison ?? null}
                  />
                </div>
              </StyledRow>
              <StyledRow>
                <div className="section-box">
                  <AssignedGoal data={goalsDataCurrentCollaborator} />
                </div>
                <div className="section-box">
                  <AssignedGoal data={goalsDataComparisonCollaborator} />
                </div>
              </StyledRow>
              <StyledRow>
                <div className="section-box">
                  <StyledWorkInformationBody
                    withMarginTop={false}
                    withMarginBottom={true}
                  >
                    <TextSwitch
                      options={resumeTextSwitch}
                      selectedOption={selectedOption}
                      setSelectedOption={setSelectedOption}
                      disabled={false}
                    />
                    <ProfessionalWay
                      resumeData={resumeDataYearCollaboratorViewed}
                    />
                    <Divider />
                    <BusinessAreaManagerComment
                      resumeData={resumeDataYearCollaboratorViewed}
                    />
                    <Divider />
                    <ProgressPlan
                      data={resumeDataYearCollaboratorViewed ?? null}
                      collaborator={{
                        id: currentCollaborator.id,
                        name: currentCollaborator.name,
                        surname: currentCollaborator.surname,
                        area: currentCollaborator.area,
                        practice: currentCollaborator.practice,
                        cc: currentCollaborator.cc,
                        internalGrade: currentCollaborator.internalGrade,
                        careerManagerFullName: null,
                      }}
                      collaboratorCmSheets={cmSheetsCollaboratorViewed}
                      planSelected={
                        resumeDataYearCollaboratorViewed?.finalInterview ?? null
                      }
                      selectedYear={selectedYear}
                      loading={showLoader}
                      pendingCalibration={pendingCalibration}
                      endInternalGrade={endInternalGradeCollaboratorViewed}
                      isComparison
                    />
                  </StyledWorkInformationBody>
                </div>
                <div className="section-box">
                  <StyledWorkInformationBody
                    withMarginTop={false}
                    withMarginBottom={true}
                  >
                    <TextSwitch
                      options={resumeTextSwitch}
                      selectedOption={selectedOption}
                      setSelectedOption={setSelectedOption}
                      disabled={false}
                    />
                    <ProfessionalWay
                      resumeData={resumeDataYearCollaboratorComparison}
                    />
                    <Divider />
                    <BusinessAreaManagerComment
                      resumeData={resumeDataYearCollaboratorComparison}
                    />
                    <Divider />
                    <ProgressPlan
                      data={resumeDataYearCollaboratorComparison ?? null}
                      collaborator={{
                        id: comparisonCollaborator.id,
                        name: comparisonCollaborator.name,
                        surname: comparisonCollaborator.surname,
                        area: comparisonCollaborator.area,
                        practice: comparisonCollaborator.practice,
                        cc: comparisonCollaborator.cc,
                        internalGrade: comparisonCollaborator.internalGrade,
                        careerManagerFullName: null,
                      }}
                      collaboratorCmSheets={comparisonCmSheets}
                      planSelected={
                        resumeDataYearCollaboratorComparison?.finalInterview ??
                        null
                      }
                      selectedYear={selectedYear}
                      loading={showLoader}
                      pendingCalibration={pendingCalibration}
                      endInternalGrade={endInternalGradeCollaboratorComparison}
                      isComparison
                    />
                  </StyledWorkInformationBody>
                </div>
              </StyledRow>
            </>
          ) : null}
        </StyledGridComparison>
      ) : null}
    </Panel>
  );
};
